import { toMap } from "@/app/apiutils";
import { EnumLocaleAwareField } from "@/app/services/extensions/localisationServiceExtended";
import { EnumBusinessType } from "@/app/services/extensions/suppliermanufacturerServiceExtended";
import { getAllGeneratedEnumValues } from "@/app/services/generated/enums/registerEnumValues.generated";
import { EnumDistributionStatus } from "@/app/types/common/distribution";
import { EnumListItemStatus } from "@/components/controls/listselector/BmListTypes";
import { EnumSnackbarType } from "@/components/controls/snackbar/SnackbarTypes";
import { EnumDialogType } from "@/components/dialogs/DialogTypes";
import EnumUserPermissionAccess from "@/components/pages/administration/user_roles/EnumUserPermissionAccess";
import { EnumProcessOutputMovementType } from "@/components/pages/batches/composables/processOutputMovementType";
import {
  EnumDistributionItemAction,
  EnumDistributionItemType,
} from "@/components/pages/distribution/types";
import { EnumIntegrationStatus } from "@/components/pages/integrations/types";
import { EnumChangelogEntryType } from "@/components/pages/news/enumChangelogEntryType";
import { EnumCommunicationLogLineType } from "@/components/pages/outlet/communications/types";
import { EnumLogEntryStatus } from "@/components/pages/outlet/crm/subcomponents/activityLogEntryCard";
import { EnumOutputMovementType } from "@/components/pages/production/assemblies/composables/enumOutputMovementType";
import { EnumVesselSummaryView } from "@/components/pages/production/vessels/enumVesselSummaryView";
import { EnumVesselMovementType } from "@/components/pages/production/vessels/logs/composables/vesselMovementState";
import { EnumCostPriceUpdateStrategyType } from "@/components/pages/stock/costpriceupdater/enumCostPriceUpdateStrategyType";
import { EnumStockType } from "@/components/pages/stock/stocktake/enumStockType";
import { translate } from "@/plugins/i18n";
import { EnumCompanyFeatureFlag } from "@da/state/companyFeaturesEnum";
import { EnumStopListItem } from "@da/state/daStopListTypes";

let valuesByEnumName: Map<string, BaseEnumType> | null = null;

export interface IEnumReviver {
  getByKey: (key: number) => BaseEnumType | null;
}

export default interface BaseEnumType<T = any> {
  key: number;
  value: string;
  translationKey?: string;
}

// Very crude implementation of isEnum - Would like a better way to do this
export function isEnum(item: any): item is BaseEnumType {
  return item.key !== undefined && item.value !== undefined;
}

function formAllEnumValueRegistry() {
  const generatedEnumValues = getAllGeneratedEnumValues();

  const craftedEnumValues: readonly BaseEnumType[] = [
    EnumLocaleAwareField.getValues(),
    EnumBusinessType.getValues(),
    EnumDistributionStatus.getValues(),
    EnumListItemStatus.getValues(),
    EnumSnackbarType.getValues(),
    EnumDialogType.getValues(),
    EnumUserPermissionAccess.getValues(),
    EnumProcessOutputMovementType.getValues(),
    EnumDistributionItemAction.getValues(),
    EnumDistributionItemType.getValues(),
    EnumIntegrationStatus.getValues(),
    EnumChangelogEntryType.getValues(),
    EnumCommunicationLogLineType.getValues(),
    EnumLogEntryStatus.getValues(),
    EnumOutputMovementType.getValues(),
    EnumVesselSummaryView.getValues(),
    EnumVesselMovementType.getValues(),
    EnumCostPriceUpdateStrategyType.getValues(),
    EnumStockType.getValues(),
    EnumCompanyFeatureFlag.getValues(),
    EnumStopListItem.getValues(),
  ].flat();

  return toMap(
    generatedEnumValues.concat(craftedEnumValues),
    (x) => x.constructor.name + "|" + x.key
  );
}

export function enumFromJson(json: any): BaseEnumType {
  if (!valuesByEnumName) {
    valuesByEnumName = formAllEnumValueRegistry();
  }

  if (json.enumName) {
    const revived = valuesByEnumName.get(json.enumName + "|" + json.key);

    if (revived) {
      return revived;
    }
  }

  // Should have found the value above, but didn't, so revert to the old style simple
  // inflation of the main enum properties.
  let result = {
    // Mark the result specially - may help in debugging if this occurs.
    revivedByInflation: true,
    key: json.key,
    value: json.value,
    translationKey: json.translationKey,
    // Our generated enum types have a translation function, so implement that on any json inflated enums
    get translation() {
      return translate(this.translationKey ?? "");
    },
  };
  return result;
}

export type BmEnumType = { getValues: () => any };
